import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';

export const isNativePlatform = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform();
export const isAndroid = () => Capacitor.getPlatform() === 'android';
export const isIOS = () => Capacitor.getPlatform() === 'ios';
export const isWeb = () => Capacitor.getPlatform() === 'web';

/**
 * initializeCapacitor — called once from main.jsx on native platforms.
 *
 * FIXES vs original:
 * 1. StatusBar: added setOverlaysWebView(false) so the status bar doesn't
 *    overlap web content. Combined with viewport-fit=cover in index.html,
 *    the content sits below the status bar and safe-area-inset-top handles
 *    the spacing correctly.
 *
 * 2. Keyboard: keyboardDidShow instead of keyboardWillShow for Android.
 *    Android doesn't reliably fire keyboardWillShow. Using keyboardDidShow
 *    ensures --keyboard-height is set after the keyboard is fully visible.
 *
 * 3. backButton: properly navigates within the React Router history stack
 *    using a custom event (avoids circular import with router).
 *
 * 4. appStateChange: clears stale document state on resume to prevent the
 *    viewer showing an old document after the app was backgrounded.
 */
export const initializeCapacitor = async () => {
  if (!isNativePlatform()) return;

  try {
    if (isAndroid()) {
      // Do NOT overlay webview — let safe-area-inset-top handle the gap
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setBackgroundColor({ color: '#1a1a2e' });
      await StatusBar.setStyle({ style: Style.Dark });
    }

    if (isIOS()) {
      await StatusBar.setStyle({ style: Style.Dark });
    }

    // ── Keyboard ────────────────────────────────────────────────────────────
    // We set --keyboard-height so CSS can add padding above the keyboard for
    // elements that need to remain visible (e.g., chat inputs, form fields).
    // This is separate from the 'native' resize mode set in capacitor.config.ts.

    Keyboard.addListener('keyboardDidShow', (info) => {
      document.documentElement.style.setProperty(
        '--keyboard-height',
        `${info.keyboardHeight}px`
      );
    });

    Keyboard.addListener('keyboardDidHide', () => {
      document.documentElement.style.setProperty('--keyboard-height', '0px');
    });

    // ── Back Button (Android) ────────────────────────────────────────────────
    // Fire a custom DOM event so the router can handle it without a circular
    // import. Listeners in App.jsx or individual screens can catch this.
    App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        // Dispatch custom event — router listener in main.jsx will handle it
        window.dispatchEvent(new CustomEvent('capacitor:backButton'));
      } else {
        App.exitApp();
      }
    });

    // ── Deep Linking ─────────────────────────────────────────────────────────
    App.addListener('appUrlOpen', (event) => {
      try {
        const url = new URL(event.url);
        const path = url.pathname + url.search;
        if (path && path !== '/') {
          // Use history API to navigate without a full reload
          window.history.pushState(null, '', path);
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
      } catch (e) {
        console.error('[Capacitor] appUrlOpen parse error:', e);
      }
    });

    // ── App Resume ───────────────────────────────────────────────────────────
    // When the app resumes from background, we may need to refresh auth state
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        // Dispatch custom event — stores can listen and refresh if needed
        window.dispatchEvent(new CustomEvent('capacitor:appResume'));
      }
    });

    if (import.meta.env.DEV) {
      console.log('[Capacitor] Initialized on', getPlatform());
    }
  } catch (error) {
    console.error('[Capacitor] Init error:', error);
  }
};

/**
 * getApiBaseUrl — returns the correct API base URL for the current platform.
 * In Capacitor, relative URLs don't work because the app is served from
 * capacitor://localhost or https://localhost, not the backend domain.
 */
export const getApiBaseUrl = () => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) return envUrl;
  if (isWeb()) return ''; // dev proxy handles it
  return 'https://lensverse.vtuae.com';
};

export default {
  isNativePlatform,
  getPlatform,
  isAndroid,
  isIOS,
  isWeb,
  initializeCapacitor,
  getApiBaseUrl,
};